import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject, interval } from 'rxjs';
import { takeUntil, switchMap } from 'rxjs/operators';
import { FileVaultApiService } from '../../services/file-vault-api.service';
import {
  Notification,
  NotificationType,
  NotificationPriority,
  NotificationSearchCriteria,
  NotificationSummary
} from '../../models/file-vault.models';

export interface NotificationClickEvent {
  notification: Notification;
  action: 'view' | 'markRead' | 'dismiss' | 'delete';
}

@Component({
  selector: 'fv-notification-center',
  standalone: true,
  template: `
    <div class="notification-center" [class.compact]="compact" [class.expanded]="isExpanded">
      <!-- Header with badge -->
      <div class="notification-header" (click)="toggleExpand()">
        <div class="header-content">
          <span class="bell-icon">🔔</span>
          <span class="header-title" *ngIf="!compact">Notifications</span>
          <span class="unread-badge" *ngIf="summary?.unreadCount" [class.urgent]="summary?.urgentCount">
            {{ (summary?.unreadCount ?? 0) > 99 ? '99+' : summary?.unreadCount }}
          </span>
        </div>
        <button class="expand-toggle" *ngIf="!compact">
          {{ isExpanded ? '▲' : '▼' }}
        </button>
      </div>

      <!-- Expanded notification panel -->
      <div class="notification-panel" *ngIf="isExpanded">
        <!-- Summary bar -->
        <div class="summary-bar">
          <div class="summary-stats">
            <span class="stat" [class.active]="!filterUnreadOnly" (click)="toggleUnreadFilter()">
              All ({{ totalCount }})
            </span>
            <span class="stat" [class.active]="filterUnreadOnly" (click)="toggleUnreadFilter()">
              Unread ({{ summary?.unreadCount || 0 }})
            </span>
            <span class="stat urgent" *ngIf="summary?.urgentCount">
              Urgent: {{ summary?.urgentCount }}
            </span>
          </div>
          <div class="summary-actions">
            <button class="action-btn" (click)="markAllRead()" [disabled]="!summary?.unreadCount" title="Mark all as read">
              ✓ All
            </button>
            <button class="action-btn" (click)="refresh()" title="Refresh">
              ↻
            </button>
          </div>
        </div>

        <!-- Filter row -->
        <div class="filter-row" *ngIf="showFilters">
          <select [(ngModel)]="selectedType" (ngModelChange)="onFilterChange()" class="filter-select">
            <option [ngValue]="undefined">All Types</option>
            <option *ngFor="let type of notificationTypes" [ngValue]="type.value">{{ type.label }}</option>
          </select>
          <select [(ngModel)]="selectedPriority" (ngModelChange)="onFilterChange()" class="filter-select">
            <option [ngValue]="undefined">All Priorities</option>
            <option *ngFor="let priority of priorities" [ngValue]="priority.value">{{ priority.label }}</option>
          </select>
        </div>

        <!-- Loading state -->
        <div class="loading-state" *ngIf="isLoading">
          <span class="spinner"></span>
          <span>Loading notifications...</span>
        </div>

        <!-- Empty state -->
        <div class="empty-state" *ngIf="!isLoading && notifications.length === 0">
          <span class="empty-icon">📭</span>
          <span class="empty-text">{{ filterUnreadOnly ? 'No unread notifications' : 'No notifications' }}</span>
        </div>

        <!-- Notification list -->
        <div class="notification-list" *ngIf="!isLoading && notifications.length > 0">
          <div
            *ngFor="let notification of notifications"
            class="notification-item"
            [class.unread]="!notification.isRead"
            [class.priority-high]="notification.priority === NotificationPriority.High"
            [class.priority-critical]="notification.priority === NotificationPriority.Critical"
            (click)="onNotificationClick(notification, 'view')">

            <div class="notification-icon" [class]="'type-' + getTypeKey(notification.type)">
              {{ getTypeIcon(notification.type) }}
            </div>

            <div class="notification-content">
              <div class="notification-title">{{ notification.title }}</div>
              <div class="notification-message" *ngIf="notification.message">{{ notification.message }}</div>
              <div class="notification-meta">
                <span class="meta-time" [title]="notification.createdAt | date:'medium'">
                  {{ getRelativeTime(notification.createdAt) }}
                </span>
                <span class="meta-entity" *ngIf="notification.entityType">
                  {{ notification.entityType }}
                </span>
                <span class="priority-badge" *ngIf="notification.priority >= NotificationPriority.High">
                  {{ getPriorityLabel(notification.priority) }}
                </span>
              </div>
            </div>

            <div class="notification-actions" (click)="$event.stopPropagation()">
              <button
                class="action-icon"
                *ngIf="!notification.isRead"
                (click)="onNotificationClick(notification, 'markRead')"
                title="Mark as read">
                ✓
              </button>
              <button
                class="action-icon"
                (click)="onNotificationClick(notification, 'dismiss')"
                title="Dismiss">
                ✕
              </button>
            </div>
          </div>
        </div>

        <!-- Load more -->
        <div class="load-more" *ngIf="hasMore && !isLoading">
          <button class="load-more-btn" (click)="loadMore()">
            Load more notifications
          </button>
        </div>

        <!-- Footer -->
        <div class="notification-footer" *ngIf="showFooter">
          <button class="footer-link" (click)="openPreferences.emit()">
            ⚙ Notification Settings
          </button>
        </div>
      </div>
    </div>
  `,
  styles: [`
    .notification-center {
      position: relative;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    }

    .notification-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 8px 12px;
      background: var(--fv-surface);
      border: 1px solid var(--fv-border);
      border-radius: 4px;
      cursor: pointer;
      user-select: none;
    }

    .notification-header:hover {
      background: var(--fv-surface-hover);
    }

    .header-content {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .bell-icon {
      font-size: 18px;
    }

    .header-title {
      font-weight: 500;
      color: var(--fv-text);
    }

    .unread-badge {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      min-width: 20px;
      height: 20px;
      padding: 0 6px;
      background: #1976d2;
      color: white;
      border-radius: 10px;
      font-size: 11px;
      font-weight: 600;
    }

    .unread-badge.urgent {
      background: #d32f2f;
      animation: pulse 2s infinite;
    }

    @keyframes pulse {
      0%, 100% { transform: scale(1); }
      50% { transform: scale(1.1); }
    }

    .expand-toggle {
      background: none;
      border: none;
      color: var(--fv-text-secondary);
      cursor: pointer;
      padding: 4px;
    }

    .notification-panel {
      position: absolute;
      top: 100%;
      right: 0;
      width: 380px;
      max-height: 500px;
      margin-top: 4px;
      background: var(--fv-surface);
      border: 1px solid var(--fv-border);
      border-radius: 8px;
      box-shadow: 0 4px 12px var(--fv-shadow);
      z-index: 1000;
      display: flex;
      flex-direction: column;
      overflow: hidden;
    }

    .compact .notification-panel {
      width: 320px;
    }

    .summary-bar {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 10px 12px;
      background: var(--fv-surface-alt);
      border-bottom: 1px solid var(--fv-border);
    }

    .summary-stats {
      display: flex;
      gap: 12px;
    }

    .stat {
      font-size: 12px;
      color: var(--fv-text-secondary);
      cursor: pointer;
      padding: 2px 6px;
      border-radius: 4px;
    }

    .stat:hover, .stat.active {
      background: var(--fv-status-info-bg);
      color: var(--fv-text-accent);
    }

    .stat.urgent {
      color: var(--fv-status-error-fg);
      font-weight: 600;
    }

    .summary-actions {
      display: flex;
      gap: 8px;
    }

    .action-btn {
      padding: 4px 8px;
      font-size: 12px;
      background: none;
      border: 1px solid var(--fv-border);
      border-radius: 4px;
      cursor: pointer;
      color: var(--fv-text-secondary);
    }

    .action-btn:hover:not(:disabled) {
      background: var(--fv-status-info-bg);
      border-color: var(--fv-text-accent);
      color: var(--fv-text-accent);
    }

    .action-btn:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    .filter-row {
      display: flex;
      gap: 8px;
      padding: 8px 12px;
      background: var(--fv-surface-alt);
      border-bottom: 1px solid var(--fv-border);
    }

    .filter-select {
      flex: 1;
      padding: 6px 8px;
      font-size: 12px;
      border: 1px solid var(--fv-border);
      border-radius: 4px;
      background: var(--fv-surface);
      color: var(--fv-text);
    }

    .loading-state, .empty-state {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 40px 20px;
      color: var(--fv-text-secondary);
      gap: 12px;
    }

    .spinner {
      width: 24px;
      height: 24px;
      border: 2px solid var(--fv-border);
      border-top-color: var(--fv-text-accent);
      border-radius: 50%;
      animation: spin 1s linear infinite;
    }

    @keyframes spin {
      to { transform: rotate(360deg); }
    }

    .empty-icon {
      font-size: 32px;
    }

    .empty-text {
      font-size: 13px;
    }

    .notification-list {
      flex: 1;
      overflow-y: auto;
      max-height: 350px;
    }

    .notification-item {
      display: flex;
      gap: 12px;
      padding: 12px;
      border-bottom: 1px solid var(--fv-border-subtle);
      cursor: pointer;
      transition: background 0.2s;
    }

    .notification-item:hover {
      background: var(--fv-surface-hover);
    }

    .notification-item.unread {
      background: var(--fv-status-info-bg);
    }

    .notification-item.unread:hover {
      background: var(--fv-surface-hover);
    }

    .notification-item.priority-high {
      border-left: 3px solid var(--fv-status-warning-fg);
    }

    .notification-item.priority-critical {
      border-left: 3px solid var(--fv-status-error-fg);
    }

    .notification-icon {
      width: 36px;
      height: 36px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 16px;
      background: var(--fv-status-info-bg);
      flex-shrink: 0;
    }

    .notification-icon.type-workflowassigned { background: var(--fv-status-success-bg); }
    .notification-icon.type-approvalrequired { background: var(--fv-status-warning-bg); }
    .notification-icon.type-statechanged { background: var(--fv-status-info-bg); }
    .notification-icon.type-taskoverdue { background: var(--fv-status-error-bg); }
    .notification-icon.type-changesubmitted { background: var(--fv-status-indigo-bg); }
    .notification-icon.type-changeapproved { background: var(--fv-status-success-bg); }
    .notification-icon.type-changerejected { background: var(--fv-status-error-bg); }
    .notification-icon.type-filecheckedout { background: var(--fv-status-info-bg); }
    .notification-icon.type-filecheckedin { background: var(--fv-status-success-bg); }
    .notification-icon.type-mention { background: var(--fv-status-error-bg); }
    .notification-icon.type-systemannouncement { background: var(--fv-status-warning-bg); }

    .notification-content {
      flex: 1;
      min-width: 0;
    }

    .notification-title {
      font-size: 13px;
      font-weight: 500;
      color: var(--fv-text);
      margin-bottom: 4px;
      line-height: 1.3;
    }

    .notification-item.unread .notification-title {
      font-weight: 600;
    }

    .notification-message {
      font-size: 12px;
      color: var(--fv-text-secondary);
      margin-bottom: 6px;
      overflow: hidden;
      text-overflow: ellipsis;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
    }

    .notification-meta {
      display: flex;
      gap: 8px;
      font-size: 11px;
      color: var(--fv-text-muted);
    }

    .meta-entity {
      background: var(--fv-border-subtle);
      padding: 1px 6px;
      border-radius: 3px;
    }

    .priority-badge {
      background: var(--fv-status-error-bg);
      color: var(--fv-status-error-fg);
      padding: 1px 6px;
      border-radius: 3px;
      font-weight: 500;
    }

    .notification-actions {
      display: flex;
      flex-direction: column;
      gap: 4px;
      opacity: 0;
      transition: opacity 0.2s;
    }

    .notification-item:hover .notification-actions {
      opacity: 1;
    }

    .action-icon {
      width: 24px;
      height: 24px;
      border: none;
      background: var(--fv-border-subtle);
      border-radius: 4px;
      cursor: pointer;
      font-size: 12px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: var(--fv-text);
    }

    .action-icon:hover {
      background: var(--fv-border);
    }

    .load-more {
      padding: 12px;
      text-align: center;
      border-top: 1px solid var(--fv-border-subtle);
    }

    .load-more-btn {
      padding: 8px 16px;
      font-size: 12px;
      color: var(--fv-text-accent);
      background: none;
      border: 1px solid var(--fv-text-accent);
      border-radius: 4px;
      cursor: pointer;
    }

    .load-more-btn:hover {
      background: var(--fv-status-info-bg);
    }

    .notification-footer {
      padding: 10px 12px;
      background: var(--fv-surface-alt);
      border-top: 1px solid var(--fv-border);
      text-align: center;
    }

    .footer-link {
      font-size: 12px;
      color: var(--fv-text-accent);
      background: none;
      border: none;
      cursor: pointer;
    }

    .footer-link:hover {
      text-decoration: underline;
    }
  `],
  imports: [CommonModule, FormsModule]
})
export class NotificationCenterComponent implements OnInit, OnDestroy {
  private api = inject(FileVaultApiService);
  private destroy$ = new Subject<void>();

  /** Compact mode shows only the bell icon with badge */
  @Input() compact = false;

  /** Start expanded */
  @Input() startExpanded = false;

  /** Show filter dropdowns */
  @Input() showFilters = true;

  /** Show footer with settings link */
  @Input() showFooter = true;

  /** Auto-refresh interval in seconds (0 = disabled) */
  @Input() autoRefreshInterval = 60;

  /** Page size for loading notifications */
  @Input() pageSize = 10;

  /** Event when a notification is clicked */
  @Output() notificationClick = new EventEmitter<NotificationClickEvent>();

  /** Event to open preferences panel */
  @Output() openPreferences = new EventEmitter<void>();

  /** Event when unread count changes */
  @Output() unreadCountChange = new EventEmitter<number>();

  // Component state
  notifications: Notification[] = [];
  summary: NotificationSummary | null = null;
  isExpanded = false;
  isLoading = false;
  totalCount = 0;
  currentPage = 1;
  hasMore = false;
  filterUnreadOnly = false;
  selectedType?: NotificationType;
  selectedPriority?: NotificationPriority;

  // Expose enums to template
  NotificationPriority = NotificationPriority;

  notificationTypes = [
    { value: NotificationType.WorkflowAssigned, label: 'Workflow Assigned' },
    { value: NotificationType.ApprovalRequired, label: 'Approval Required' },
    { value: NotificationType.StateChanged, label: 'State Changed' },
    { value: NotificationType.TaskOverdue, label: 'Task Overdue' },
    { value: NotificationType.ChangeSubmitted, label: 'Change Submitted' },
    { value: NotificationType.ChangeApproved, label: 'Change Approved' },
    { value: NotificationType.ChangeRejected, label: 'Change Rejected' },
    { value: NotificationType.FileCheckedOut, label: 'File Checked Out' },
    { value: NotificationType.FileCheckedIn, label: 'File Checked In' },
    { value: NotificationType.Mention, label: 'Mention' },
    { value: NotificationType.SystemAnnouncement, label: 'System Announcement' }
  ];

  priorities = [
    { value: NotificationPriority.Low, label: 'Low' },
    { value: NotificationPriority.Medium, label: 'Medium' },
    { value: NotificationPriority.High, label: 'High' },
    { value: NotificationPriority.Critical, label: 'Critical' }
  ];

  private typeIcons: Record<number, string> = {
    [NotificationType.WorkflowAssigned]: '📋',
    [NotificationType.ApprovalRequired]: '✋',
    [NotificationType.StateChanged]: '🔄',
    [NotificationType.TaskOverdue]: '⏰',
    [NotificationType.ChangeSubmitted]: '📝',
    [NotificationType.ChangeApproved]: '✅',
    [NotificationType.ChangeRejected]: '❌',
    [NotificationType.FileCheckedOut]: '📤',
    [NotificationType.FileCheckedIn]: '📥',
    [NotificationType.Mention]: '💬',
    [NotificationType.PermissionGranted]: '🔓',
    [NotificationType.PermissionRevoked]: '🔒',
    [NotificationType.DailyDigest]: '📰',
    [NotificationType.WeeklyDigest]: '📰',
    [NotificationType.SystemAnnouncement]: '📢',
    [NotificationType.Custom]: '📌'
  };

  ngOnInit(): void {
    this.isExpanded = this.startExpanded;
    this.loadSummary();
    this.loadNotifications();
    this.setupAutoRefresh();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  toggleExpand(): void {
    this.isExpanded = !this.isExpanded;
    if (this.isExpanded) {
      this.refresh();
    }
  }

  toggleUnreadFilter(): void {
    this.filterUnreadOnly = !this.filterUnreadOnly;
    this.currentPage = 1;
    this.notifications = [];
    this.loadNotifications();
  }

  onFilterChange(): void {
    this.currentPage = 1;
    this.notifications = [];
    this.loadNotifications();
  }

  async refresh(): Promise<void> {
    this.currentPage = 1;
    this.notifications = [];
    await Promise.all([
      this.loadSummary(),
      this.loadNotifications()
    ]);
  }

  async loadSummary(): Promise<void> {
    try {
      this.summary = await this.api.getNotificationSummary().toPromise() ?? null;
      this.unreadCountChange.emit(this.summary?.unreadCount ?? 0);
    } catch (error) {
      console.error('Failed to load notification summary', error);
    }
  }

  async loadNotifications(): Promise<void> {
    this.isLoading = true;
    try {
      const criteria: NotificationSearchCriteria = {
        pageNumber: this.currentPage,
        pageSize: this.pageSize,
        isRead: this.filterUnreadOnly ? false : undefined,
        isDismissed: false,
        type: this.selectedType,
        priority: this.selectedPriority
      };

      const result = await this.api.getNotifications(criteria).toPromise();
      if (result) {
        if (this.currentPage === 1) {
          this.notifications = result.items;
        } else {
          this.notifications = [...this.notifications, ...result.items];
        }
        this.totalCount = result.totalCount;
        this.hasMore = this.notifications.length < result.totalCount;
      }
    } catch (error) {
      console.error('Failed to load notifications', error);
    } finally {
      this.isLoading = false;
    }
  }

  loadMore(): void {
    if (!this.isLoading && this.hasMore) {
      this.currentPage++;
      this.loadNotifications();
    }
  }

  async markAllRead(): Promise<void> {
    try {
      await this.api.markAllAsRead().toPromise();
      this.notifications.forEach(n => n.isRead = true);
      await this.loadSummary();
    } catch (error) {
      console.error('Failed to mark all as read', error);
    }
  }

  async onNotificationClick(notification: Notification, action: 'view' | 'markRead' | 'dismiss' | 'delete'): Promise<void> {
    this.notificationClick.emit({ notification, action });

    try {
      switch (action) {
        case 'view':
          if (!notification.isRead) {
            await this.api.markAsRead(notification.id).toPromise();
            notification.isRead = true;
            await this.loadSummary();
          }
          break;
        case 'markRead':
          await this.api.markAsRead(notification.id).toPromise();
          notification.isRead = true;
          await this.loadSummary();
          break;
        case 'dismiss':
          await this.api.dismissNotification(notification.id).toPromise();
          this.notifications = this.notifications.filter(n => n.id !== notification.id);
          await this.loadSummary();
          break;
        case 'delete':
          await this.api.deleteNotification(notification.id).toPromise();
          this.notifications = this.notifications.filter(n => n.id !== notification.id);
          await this.loadSummary();
          break;
      }
    } catch (error) {
      console.error(`Failed to ${action} notification`, error);
    }
  }

  getTypeIcon(type: NotificationType): string {
    return this.typeIcons[type] ?? '📌';
  }

  getTypeKey(type: NotificationType): string {
    return NotificationType[type]?.toLowerCase() ?? 'custom';
  }

  getPriorityLabel(priority: NotificationPriority): string {
    const labels: Record<number, string> = {
      [NotificationPriority.Low]: 'Low',
      [NotificationPriority.Medium]: 'Medium',
      [NotificationPriority.High]: 'High',
      [NotificationPriority.Critical]: 'Critical'
    };
    return labels[priority] ?? '';
  }

  getRelativeTime(date: Date | string): string {
    const now = new Date();
    const then = new Date(date);
    const diffMs = now.getTime() - then.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return then.toLocaleDateString();
  }

  private setupAutoRefresh(): void {
    if (this.autoRefreshInterval > 0) {
      interval(this.autoRefreshInterval * 1000)
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          if (this.isExpanded) {
            this.loadSummary();
          } else {
            // Just update the badge count when collapsed
            this.loadSummary();
          }
        });
    }
  }
}
